import { select, call, take, all, getContext, spawn } from 'redux-saga/effects';
import { DISABLE_FULLSTORY } from 'next/entities/features';
import { selectUser, selectUserId } from 'reducers/user';
import { REPLACE_ACCOUNT_META } from 'constants/account/meta';
import {
  REPLACE_ACCOUNT_USERS,
  REPLACE_ACCOUNT_CHECKLISTS,
} from 'constants/actionTypes';
import { patterns as flowPatterns } from 'actions/account/flows';
import { patterns as accountsPatterns } from 'actions/accounts';
import { patterns as segmentPatterns } from 'actions/account/segments';
import { USER_REPLACED } from 'actions/user';
import { CHROME_EXTENSION_STATUS_UPDATED } from 'constants/chromeExtension/actionTypes';
import { selectAccountMeta } from 'reducers/account/meta';
import { selectIsInstalled } from 'reducers/account/installedDomains';
import { selectAccountUsers } from 'reducers/account/users';
import { selectFlows } from 'reducers/account/flows';
import { selectAccountChecklists } from 'reducers/account/checklists';
import { selectAccountSegments } from 'reducers/account/segments';
import {
  selectAccountFeature,
  selectAccountFeatures,
} from 'reducers/account/features';
import {
  selectChromeExtensionVersion,
  selectHasChromeExtensionInstalled,
} from 'reducers/chromeExtension';
import { selectNumAccounts } from 'reducers/accounts';

function* disableFullStory() {
  const isFullStoryDisabled = yield select(
    selectAccountFeature,
    DISABLE_FULLSTORY
  );

  if (isFullStoryDisabled) {
    window.FS?.shutdown();
  }
}

function* identifyAll() {
  const auth = yield getContext('auth');
  const isSpoofing = yield call(auth.isSpoofing);

  if (!isSpoofing) {
    const {
      id: account_id,
      buffer: account_buffer,
      company,
      createdAt: account_createdAt,
      flowsShown: account_flowsShown = 0,
      flowsShownLimit: account_flowsShownLimit = 50,
      isTrial: account_isTrial,
      isTrialExpired: account_isTrialExpired,
      lastSeenTimestamp: account_lastSeenTimestamp,
      npsScore: account_npsScore,
      stripeId: account_stripeId,
      stripePlanId: account_stripePlanId,
      stripeSubCreatedAt: account_stripeSubCreatedAt,
    } = yield select(selectAccountMeta) || {};

    const users = yield select(selectAccountUsers) || {};

    const {
      email,
      firstUsedComponents,
      fullname,
      isInvitedUser,
      lastSeenTimestamp,
      role,
      createdAt,
    } = yield select(selectUser) || {};

    const id = yield select(selectUserId);

    const flows = yield select(selectFlows) || {};
    const checklists = yield select(selectAccountChecklists) || {};
    const segments = yield select(selectAccountSegments) || {};

    const features = yield select(selectAccountFeatures) || {};
    const { HAS_BETA_FEATURES } = features;

    const hasCrxInstalled = yield select(selectHasChromeExtensionInstalled);
    const crxVersion = yield select(selectChromeExtensionVersion);

    const numberOfAccounts = yield select(selectNumAccounts);

    const account_isInstalled = yield select(selectIsInstalled);

    // for privacy reasons, some users have been allowed to opt-out of
    // studio analytics providers.  exclusions take the form of feature
    // flags and are instrumented below...

    const isFullStoryDisabled = yield select(
      selectAccountFeature,
      DISABLE_FULLSTORY
    );

    // use a reference to window.analytics instead of defining it as an external
    // the analytics.js snippet defines "queueing stubs" and uses a
    // createElement to load itself asynchronously.

    // on load, the stub invocations are replayed, but since webpack has wrapped
    // the snippet in a non-window context, analytics.js can't access the
    // queues, and all invocations (read: identifies) are lost.

    if (window.analytics && window.analytics.identify) {
      const commonTraits = {
        account_createdAt,
        ...Object.keys(features).reduce((acc, feature) => {
          acc[`account_feature_${feature}`] = features[feature];
          return acc;
        }, {}),
        account_flowsShown,
        account_flowsShownLimit,
        account_isInstalled,
        account_isTrial,
        account_isTrialExpired,
        account_lastSeenTimestamp,
        account_npsScore,
        account_stripeId,
        account_stripePlanId,
        account_stripeSubCreatedAt,
        company,
        isTrial: account_isTrial,
        isTrialExpired: account_isTrialExpired,
        stripeId: account_stripeId,
        stripePlanId: account_stripePlanId,
        totalFlows: Object.keys(flows).length,
        totalFlowsPublished: Object.values(flows).filter(
          ({ published }) => published
        ).length,
        users_count: Object.keys(users).length,
      };

      const identifyTraits = {
        ...commonTraits,
        Account_created_at: account_createdAt,
        accountId: account_id,
        account_buffer,
        account_id,
        account_isBeta: HAS_BETA_FEATURES,
        appcuesId: account_id,
        createdAt,
        created_at: createdAt,
        crxVersion,
        crxVersionFromClient: crxVersion,
        email,
        embedCodeInstalled: account_isInstalled,
        firstUsedComponents,
        fullname,
        hasCrxInstalled,
        id,
        isAdmin: role === 'ACCOUNT_ADMIN',
        isBeta: HAS_BETA_FEATURES,
        isInstalled: account_isInstalled,
        isInvitedUser,
        is_trial: account_isTrial,
        lastSeenTimestamp,
        npsScore: account_npsScore,
        numFlows: Object.keys(flows).length,
        numberOfAccounts,
        hasMultipleAccounts: numberOfAccounts > 1,
        only_user_in_account: Object.keys(users) === 1,
        published_checklist_count: Object.values(checklists).filter(
          ({ published }) => published
        ).length,
        published_journey_count: Object.values(flows).filter(
          ({ published }) => published
        ).length,
        role,
        segments_count: Object.keys(segments).length,
        unpublished_journey_count: Object.values(flows).filter(
          ({ published }) => !published
        ).length,
        userEmail: email,
        userId: id,
        userRole: role,
      };

      const groupTraits = commonTraits;

      const options = {
        integrations: { FullStory: !isFullStoryDisabled },
      };

      // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#managing-data-flow-with-the-integrations-object
      window.analytics.identify(id, identifyTraits, options);

      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/models/account.coffee#L97-L98
      window.analytics.group(account_id, groupTraits, options);
    }

    if (window.ga && window.ga.set) {
      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/models/account.coffee#L100-L101
      window.ga.set('&uid', account_id);
    }

    if (account_stripeId && window.profitwell) {
      /* global PROFITWELL_AUTH_TOKEN */

      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/assets/index.html#L448
      window.profitwell('auth_token', `${PROFITWELL_AUTH_TOKEN}`);

      // https://github.com/appcues/my.appcues.com/blob/171d8e239402198c83e4d4e4c205398983e1795b/app/models/account.coffee#L103-L106
      window.profitwell('user_stripe_id', account_stripeId);
    }
  }
}

// these actions fire once per session as they describe the resolution
// of data relating to the USER, not the ACCOUNT
const USER_ACTIONS = [
  USER_REPLACED,
  CHROME_EXTENSION_STATUS_UPDATED,
  accountsPatterns.resolve,
];

// these actions fire every time the ACCOUNT changes
const ACCOUNT_ACTIONS = [
  REPLACE_ACCOUNT_META,
  REPLACE_ACCOUNT_USERS,
  REPLACE_ACCOUNT_CHECKLISTS,
  flowPatterns.resolve,
  segmentPatterns.resolve,
];

export default function* identifier() {
  yield spawn(disableFullStory);
  yield all([...USER_ACTIONS.map(take), ...ACCOUNT_ACTIONS.map(take)]);
  yield call(identifyAll);
}
